import { Helmet } from "react-helmet-async";

const SITE = "https://lovac.it";
const DEFAULT_OG = `${SITE}/images/logo-orizzontale.png`;

/**
 * SEO component, title, meta description, canonical, OG, Twitter, JSON-LD.
 * Tutto si applica al document immediatamente al mount.
 */
export default function Seo({
  title,
  description,
  path = "/",
  ogImage = DEFAULT_OG,
  noindex = false,
  type = "website",
  jsonLd = null,
}) {
  const canonical = `${SITE}${path}`;
  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonical} />
      {noindex && <meta name="robots" content="noindex,nofollow" />}

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content="Lovac" />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:locale" content="it_IT" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />

      {jsonLd && (
        <script type="application/ld+json">
          {JSON.stringify(jsonLd)}
        </script>
      )}
    </Helmet>
  );
}

/* ---------- JSON-LD builders ---------- */

export const ORG_JSONLD = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "Lovac",
  url: SITE,
  logo: `${SITE}/images/logo-orizzontale.png`,
  description:
    "Lovac, Agenzia di head hunting tech-driven per PMI italiane. Database di 10 milioni di figure professionali, AI per screening, recruiter dedicati.",
  sameAs: ["https://www.linkedin.com/company/lovac"],
  contactPoint: [{
    "@type": "ContactPoint",
    contactType: "customer service",
    email: "[email protected]",
    areaServed: "IT",
    availableLanguage: ["Italian"],
  }],
};

export const WEBSITE_JSONLD = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: "Lovac",
  url: SITE,
  inLanguage: "it-IT",
};

export const breadcrumbJsonLd = (items) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map((it, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: it.name,
    item: `${SITE}${it.path}`,
  })),
});

export const serviceJsonLd = ({ name, description, path }) => ({
  "@context": "https://schema.org",
  "@type": "Service",
  serviceType: name,
  provider: { "@type": "Organization", name: "Lovac", url: SITE },
  areaServed: "IT",
  name,
  description,
  url: `${SITE}${path}`,
});

export const caseStudyJsonLd = (c) => ({
  "@context": "https://schema.org",
  "@type": "Article",
  headline: `Come ${c.nome_azienda} ha trovato un ${c.ruolo_cercato} in ${c.time_to_hire}`,
  description: c.citazione_principale || c.testo_problema?.slice(0, 160),
  author: { "@type": "Organization", name: "Lovac", url: SITE },
  publisher: {
    "@type": "Organization",
    name: "Lovac",
    logo: { "@type": "ImageObject", url: `${SITE}/images/logo-orizzontale.png` },
  },
  url: `${SITE}/casi-studio/${c.slug}`,
  inLanguage: "it-IT",
  about: c.settore,
});
